import { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import ColumnVisibilityToggle from '../../../community/residents/components/ColumnVisibilityToggle.jsx';
import FilterPopover from '../../../community/residents/components/FilterPopover.jsx';
import ActiveFilterBar from '../../../community/residents/components/ActiveFilterBar.jsx';
import { exportReasonCodesCsv } from '../../../../services/csvExport.js';
import usePersistedColumns from '../../../../hooks/usePersistedColumns.js';

/**
 * Column definitions for the reason codes table.
 * Each column has: key, label, sortable, filterable, required (can't hide).
 */
const REASON_COLUMN_DEFS = [
    { key: 'reason_code_description', label: 'Reason', sortable: true, filterable: true, required: true },
    { key: 'pal_reason_description', label: 'Reason Category', sortable: true, filterable: true, required: false },
    { key: 'reason_type_name', label: 'Reason Type', sortable: true, filterable: true, required: false },
    { key: 'scope_label', label: 'Scope', sortable: true, filterable: true, required: false },
];

const DEFAULT_VISIBLE = ['reason_code_description', 'pal_reason_description', 'reason_type_name', 'scope_label'];
const ALL_COLUMN_KEYS = REASON_COLUMN_DEFS.map((c) => c.key);

const CORPORATE_EDIT_MIN_ROLE = 30;

/** Column labels for ActiveFilterBar */
const COLUMN_LABELS = {};
REASON_COLUMN_DEFS.forEach((col) => { COLUMN_LABELS[col.key] = col.label; });

// ---------------------------------------------------------------------------
// Row-level kebab menu
// ---------------------------------------------------------------------------
const ReasonRowActions = ({ reason, onEdit, onDelete, readOnly }) => {
    const [open, setOpen] = useState(false);
    const [menuStyle, setMenuStyle] = useState({});
    const menuRef = useRef(null);

    // Close on outside click
    useEffect(() => {
        const handleClickOutside = (e) => {
            if (menuRef.current && !menuRef.current.contains(e.target)) {
                setOpen(false);
            }
        };
        if (open) {
            document.addEventListener('mousedown', handleClickOutside);
        }
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, [open]);

    // Close on scroll — avoids the detached fixed-position menu drifting off the trigger
    useEffect(() => {
        if (!open) return;
        const onScroll = () => setOpen(false);
        window.addEventListener('scroll', onScroll, true);
        return () => window.removeEventListener('scroll', onScroll, true);
    }, [open]);

    // Position the floating menu relative to the trigger. Kebab is on the left
    // of the row, so open the menu down-and-to-the-right; flip up if no room below.
    const handleToggle = () => {
        if (!open && menuRef.current) {
            const rect = menuRef.current.getBoundingClientRect();
            const spaceBelow = window.innerHeight - rect.bottom;
            const flipUp = spaceBelow < 140;
            setMenuStyle({
                position: 'fixed',
                left: `${rect.left}px`,
                top: flipUp ? 'auto' : `${rect.bottom + 4}px`,
                bottom: flipUp ? `${window.innerHeight - rect.top + 4}px` : 'auto',
                right: 'auto',
                margin: 0,
                zIndex: 9999,
            });
        }
        setOpen(!open);
    };

    if (readOnly) {
        return (
            <div className="ap-row-actions">
                <button
                    type="button"
                    className="ap-row-actions__trigger"
                    aria-label={`Actions for ${reason.reason_code_description} unavailable — corporate-set reason`}
                    title="Corporate-set reason codes can only be edited by corporate-level users"
                    disabled
                >
                    <i className="fe fe-lock" aria-hidden="true" />
                </button>
            </div>
        );
    }

    return (
        <div className="ap-row-actions" ref={menuRef}>
            <button
                type="button"
                onClick={handleToggle}
                className="ap-row-actions__trigger"
                aria-label={`Actions for ${reason.reason_code_description}`}
                aria-expanded={open}
            >
                <i className="fe fe-more-vertical" aria-hidden="true" />
            </button>

            {open ? (
                <div className="ap-row-actions__menu" style={menuStyle}>
                    <button
                        type="button"
                        onClick={() => { onEdit(reason); setOpen(false); }}
                        className="ap-row-actions__item"
                    >
                        <svg className="ap-icon-md ap-row-actions__item-icon" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" aria-hidden="true">
                            <path strokeLinecap="round" strokeLinejoin="round" d="M16.862 4.487l1.687-1.688a1.875 1.875 0 112.652 2.652L10.582 16.07a4.5 4.5 0 01-1.897 1.13L6 18l.8-2.685a4.5 4.5 0 011.13-1.897l8.932-8.931zm0 0L19.5 7.125M18 14v4.75A2.25 2.25 0 0115.75 21H5.25A2.25 2.25 0 013 18.75V8.25A2.25 2.25 0 015.25 6H10" />
                        </svg>
                        Edit Reason
                    </button>
                    <button
                        type="button"
                        onClick={() => { onDelete(reason); setOpen(false); }}
                        className="ap-row-actions__item ap-row-actions__item--danger"
                    >
                        <svg className="ap-icon-md ap-row-actions__item-icon" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" aria-hidden="true">
                            <path strokeLinecap="round" strokeLinejoin="round" d="M14.74 9l-.346 9m-4.788 0L9.26 9m9.968-3.21c.342.052.682.107 1.022.166m-1.022-.165L18.16 19.673a2.25 2.25 0 01-2.244 2.077H8.084a2.25 2.25 0 01-2.244-2.077L4.772 5.79m14.456 0a48.108 48.108 0 00-3.478-.397m-12 .562c.34-.059.68-.114 1.022-.165m0 0a48.11 48.11 0 013.478-.397m7.5 0v-.916c0-1.18-.91-2.164-2.09-2.201a51.964 51.964 0 00-3.32 0c-1.18.037-2.09 1.022-2.09 2.201v.916m7.5 0a48.667 48.667 0 00-7.5 0" />
                        </svg>
                        Delete Reason
                    </button>
                </div>
            ) : null}
        </div>
    );
};

// ---------------------------------------------------------------------------
// Main component
// ---------------------------------------------------------------------------
const CloudReasonSettingsTab = ({
    reasonCodes,
    globalReasons,
    reasonCodeTypes,
    roleWeight = 0,
    onAddReason,
    onEditReason,
    onDeleteReasons,
    saving,
}) => {
    const canEditCorporate = roleWeight > CORPORATE_EDIT_MIN_ROLE;
    const [search, setSearch] = useState('');
    const [selectedIds, setSelectedIds] = useState([]);
    const [sortColumn, setSortColumn] = useState('reason_code_description');
    const [sortOrder, setSortOrder] = useState('asc');
    const [filters, setFilters] = useState({});
    const [visibleColumns, handleColumnToggle] = usePersistedColumns('settings-cloud-reasons', DEFAULT_VISIBLE, ALL_COLUMN_KEYS);

    // Build category lookup: pal_reason_id → pal_reason_description
    const categoryLookup = useMemo(() => {
        const map = {};
        (globalReasons || []).forEach((g) => {
            map[g.pal_reason_id] = g.pal_reason_description;
        });
        return map;
    }, [globalReasons]);

    // Build reason type lookup: reason_code_type_id → reason_type_name
    const typeLookup = useMemo(() => {
        const map = {};
        (reasonCodeTypes || []).forEach((t) => {
            map[t.reason_code_type_id] = t.reason_type_name;
        });
        return map;
    }, [reasonCodeTypes]);

    // Enrich data with resolved category name, reason type, and scope
    const enrichedData = useMemo(() => {
        return (reasonCodes || []).map((rc) => {
            const isCorporate = rc.corporate_id != null && rc.corporate_id !== 0;
            const resolvedTypeName =
                rc.reason_type_name ||
                typeLookup[rc.reason_code_type_id] ||
                '—';
            return {
                ...rc,
                pal_reason_description: categoryLookup[rc.pal_reason_code_id] || 'Unknown',
                reason_type_name: resolvedTypeName,
                scope_label: isCorporate ? 'Corporate' : 'Community',
                _isCorporate: isCorporate,
            };
        });
    }, [reasonCodes, categoryLookup, typeLookup]);

    // Clear selection on data reload
    useEffect(() => {
        setSelectedIds([]);
    }, [reasonCodes]);

    // Active columns based on visibility
    const activeColumns = useMemo(
        () => REASON_COLUMN_DEFS.filter((col) => visibleColumns.includes(col.key)),
        [visibleColumns]
    );

    // Collect unique filter values from the full (unfiltered) dataset
    const getFilterValues = useCallback(
        (columnKey) => {
            const values = new Set();
            enrichedData.forEach((row) => {
                const val = row[columnKey];
                values.add(val != null && val !== '' ? String(val) : '(empty)');
            });
            return [...values].sort((a, b) =>
                a.localeCompare(b, undefined, { numeric: true, sensitivity: 'base' })
            );
        },
        [enrichedData]
    );

    // ======================================================================
    // Filtered + sorted data pipeline
    // ======================================================================
    const displayData = useMemo(() => {
        let data = [...enrichedData];

        // 1. Search
        if (search.trim()) {
            const term = search.toLowerCase();
            data = data.filter(
                (rc) =>
                    (rc.reason_code_description || '').toLowerCase().includes(term) ||
                    (rc.pal_reason_description || '').toLowerCase().includes(term) ||
                    (rc.reason_type_name || '').toLowerCase().includes(term) ||
                    (rc.scope_label || '').toLowerCase().includes(term)
            );
        }

        // 2. Column filters
        Object.entries(filters).forEach(([columnKey, values]) => {
            if (!values || values.length === 0) return;
            data = data.filter((row) => {
                const val = row[columnKey];
                const strVal = val != null && val !== '' ? String(val) : '(empty)';
                return values.includes(strVal);
            });
        });

        // 3. Sort
        data.sort((a, b) => {
            const aVal = a[sortColumn] || '';
            const bVal = b[sortColumn] || '';
            const result = String(aVal).localeCompare(String(bVal), undefined, {
                numeric: true,
                sensitivity: 'base',
            });
            return sortOrder === 'asc' ? result : -result;
        });

        return data;
    }, [enrichedData, search, filters, sortColumn, sortOrder]);

    // ======================================================================
    // Selection — rows user can't edit (corporate rows w/o privilege) are not selectable
    // ======================================================================
    const isRowEditable = useCallback((row) => !row._isCorporate || canEditCorporate, [canEditCorporate]);

    const selectableDisplay = useMemo(() => displayData.filter(isRowEditable), [displayData, isRowEditable]);

    const allSelected = selectableDisplay.length > 0 && selectableDisplay.every((r) => selectedIds.includes(r.cloud_reason_code_id));
    const someSelected = selectedIds.length > 0 && !allSelected;

    const handleSelectRow = useCallback((id) => {
        setSelectedIds((prev) =>
            prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
        );
    }, []);

    const handleSelectAll = useCallback(() => {
        const allIds = selectableDisplay.map((r) => r.cloud_reason_code_id);
        const currentAllSelected = allIds.length > 0 && allIds.every((id) => selectedIds.includes(id));
        if (currentAllSelected) {
            setSelectedIds((prev) => prev.filter((id) => !allIds.includes(id)));
        } else {
            setSelectedIds((prev) => [...new Set([...prev, ...allIds])]);
        }
    }, [selectableDisplay, selectedIds]);

    // ======================================================================
    // Sort / Filter / Column handlers
    // ======================================================================
    const handleSort = useCallback((column) => {
        if (sortColumn === column) {
            setSortOrder((o) => (o === 'asc' ? 'desc' : 'asc'));
        } else {
            setSortColumn(column);
            setSortOrder('asc');
        }
    }, [sortColumn]);

    const handleFilterChange = useCallback((columnKey, values) => {
        setFilters((prev) => ({ ...prev, [columnKey]: values }));
    }, []);

    const handleRemoveFilter = useCallback((columnKey, value) => {
        setFilters((prev) => ({
            ...prev,
            [columnKey]: (prev[columnKey] || []).filter((v) => v !== value),
        }));
    }, []);

    const handleClearAllFilters = useCallback(() => {
        setFilters({});
        setSearch('');
    }, []);

    // ======================================================================
    // Actions
    // ======================================================================
    const handleBulkDelete = () => {
        if (selectedIds.length === 0) return;
        onDeleteReasons(selectedIds);
    };

    const handleSingleDelete = (reason) => {
        onDeleteReasons([reason.cloud_reason_code_id]);
    };

    const handleExportAll = useCallback(() => {
        if (displayData.length) exportReasonCodesCsv(displayData);
    }, [displayData]);

    const handleBulkExport = useCallback(() => {
        const selected = enrichedData.filter((r) => selectedIds.includes(r.cloud_reason_code_id));
        if (selected.length) exportReasonCodesCsv(selected, 'reason-codes-selected.csv');
    }, [selectedIds, enrichedData]);

    // ======================================================================
    // Sort icons
    // ======================================================================
    const renderSortIcon = (columnKey) => {
        if (sortColumn !== columnKey) {
            return (
                <svg className="ap-icon-sm ap-sort-icon ap-sort-icon--neutral" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor" aria-hidden="true">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M8.25 15L12 18.75 15.75 15m-7.5-6L12 5.25 15.75 9" />
                </svg>
            );
        }
        return sortOrder === 'asc' ? (
            <svg className="ap-icon-sm ap-sort-icon ap-sort-icon--active" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor" aria-hidden="true">
                <path strokeLinecap="round" strokeLinejoin="round" d="M4.5 15.75l7.5-7.5 7.5 7.5" />
            </svg>
        ) : (
            <svg className="ap-icon-sm ap-sort-icon ap-sort-icon--active" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor" aria-hidden="true">
                <path strokeLinecap="round" strokeLinejoin="round" d="M19.5 8.25l-7.5 7.5-7.5-7.5" />
            </svg>
        );
    };

    // ======================================================================
    // Render
    // ======================================================================
    const hasFilters = search.trim() || Object.values(filters).some((v) => v.length > 0);

    return (
        <>
            {/* Controls row */}
            <div className="ap-controls">
                <div className="ap-controls__left">
                    <div className="ap-search">
                        <svg className="ap-icon-md ap-search__icon" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor" aria-hidden="true">
                            <path strokeLinecap="round" strokeLinejoin="round" d="M21 21l-5.197-5.197m0 0A7.5 7.5 0 105.196 5.196a7.5 7.5 0 0010.607 10.607z" />
                        </svg>
                        <input
                            type="search"
                            placeholder="Search reasons..."
                            value={search}
                            onChange={(e) => setSearch(e.target.value)}
                            className="ap-search__input"
                            aria-label="Search reasons"
                        />
                    </div>
                </div>
                <div className="ap-controls__right">
                    <ColumnVisibilityToggle
                        columns={REASON_COLUMN_DEFS}
                        visibleColumns={visibleColumns}
                        onToggle={handleColumnToggle}
                    />
                    <button type="button" className="ap-btn ap-btn--secondary" onClick={handleExportAll} aria-label="Export reasons">
                        <svg className="ap-icon-md" style={{ color: 'var(--color-text-secondary)' }} fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" aria-hidden="true">
                            <path strokeLinecap="round" strokeLinejoin="round" d="M3 16.5v2.25A2.25 2.25 0 005.25 21h13.5A2.25 2.25 0 0021 18.75V16.5M16.5 12L12 16.5m0 0L7.5 12m4.5 4.5V3" />
                        </svg>
                        Export
                    </button>
                    <button type="button" onClick={onAddReason} className="ap-btn ap-btn--primary" aria-label="Add new reason">
                        <svg className="ap-icon-md" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor" aria-hidden="true">
                            <path strokeLinecap="round" strokeLinejoin="round" d="M12 4.5v15m7.5-7.5h-15" />
                        </svg>
                        Add Reason
                    </button>
                </div>
            </div>

            {/* Active filter chips */}
            <ActiveFilterBar
                filters={filters}
                columnLabels={COLUMN_LABELS}
                onRemoveFilter={handleRemoveFilter}
                onClearAll={handleClearAllFilters}
            />

            {/* Bulk action bar */}
            {selectedIds.length > 0 ? (
                <div className="ap-bulk-bar">
                    <div className="ap-bulk-bar__left">
                        <span className="ap-bulk-bar__count">
                            {selectedIds.length} reason{selectedIds.length !== 1 ? 's' : ''} selected
                        </span>
                        <button type="button" onClick={() => setSelectedIds([])} className="ap-bulk-bar__deselect">
                            Deselect All
                        </button>
                    </div>
                    <div className="ap-bulk-bar__right">
                        <button type="button" onClick={handleBulkExport} className="ap-bulk-btn" aria-label="Export selected reasons">
                            <svg className="ap-icon-sm" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor" aria-hidden="true">
                                <path strokeLinecap="round" strokeLinejoin="round" d="M3 16.5v2.25A2.25 2.25 0 005.25 21h13.5A2.25 2.25 0 0021 18.75V16.5M16.5 12L12 16.5m0 0L7.5 12m4.5 4.5V3" />
                            </svg>
                            Export
                        </button>
                        <button type="button" onClick={handleBulkDelete} disabled={saving} className="ap-bulk-btn ap-bulk-btn--danger" aria-label="Delete selected reasons">
                            <svg className="ap-icon-sm" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor" aria-hidden="true">
                                <path strokeLinecap="round" strokeLinejoin="round" d="M14.74 9l-.346 9m-4.788 0L9.26 9m9.968-3.21c.342.052.682.107 1.022.166m-1.022-.165L18.16 19.673a2.25 2.25 0 01-2.244 2.077H8.084a2.25 2.25 0 01-2.244-2.077L4.772 5.79m14.456 0a48.108 48.108 0 00-3.478-.397m-12 .562c.34-.059.68-.114 1.022-.165m0 0a48.11 48.11 0 013.478-.397m7.5 0v-.916c0-1.18-.91-2.164-2.09-2.201a51.964 51.964 0 00-3.32 0c-1.18.037-2.09 1.022-2.09 2.201v.916m7.5 0a48.667 48.667 0 00-7.5 0" />
                            </svg>
                            Delete
                        </button>
                    </div>
                </div>
            ) : null}

            {/* Table */}
            {displayData.length === 0 ? (
                <div className="ss-empty" style={{ marginTop: 'var(--space-4)' }}>
                    <p className="ss-empty__text">
                        {hasFilters
                            ? 'No reasons match your filters.'
                            : 'No custom reason codes configured.'}
                    </p>
                </div>
            ) : (
                <div className="ap-table-wrap">
                    <table className="ap-table">
                        <thead className="ap-thead">
                            <tr>
                                {/* Checkbox column */}
                                <th scope="col" className="ap-th ap-th--checkbox">
                                    <input
                                        type="checkbox"
                                        checked={allSelected}
                                        ref={(el) => { if (el) el.indeterminate = someSelected; }}
                                        onChange={handleSelectAll}
                                        className="ap-checkbox"
                                        aria-label="Select all reasons"
                                    />
                                </th>

                                {/* Actions column — positioned next to checkbox */}
                                <th scope="col" className="ap-th ap-th--actions">
                                    <span className="ap-sr-only">Actions</span>
                                </th>

                                {/* Data columns */}
                                {activeColumns.map((col) => (
                                    <th key={col.key} scope="col" className="ap-th">
                                        <div className="ap-th__content">
                                            {col.sortable ? (
                                                <button
                                                    type="button"
                                                    onClick={() => handleSort(col.key)}
                                                    className="ap-th__sort-btn"
                                                    aria-label={`Sort by ${col.label}`}
                                                >
                                                    {col.label}
                                                    {renderSortIcon(col.key)}
                                                </button>
                                            ) : (
                                                <span>{col.label}</span>
                                            )}

                                            {col.filterable ? (
                                                <FilterPopover
                                                    columnKey={col.key}
                                                    label={col.label}
                                                    values={getFilterValues(col.key)}
                                                    selectedValues={filters[col.key] || []}
                                                    onChange={handleFilterChange}
                                                />
                                            ) : null}
                                        </div>
                                    </th>
                                ))}
                            </tr>
                        </thead>

                        <tbody className="ap-tbody">
                            {displayData.map((row) => {
                                const isSelected = selectedIds.includes(row.cloud_reason_code_id);
                                const rowEditable = isRowEditable(row);
                                return (
                                    <tr
                                        key={row.cloud_reason_code_id}
                                        className={`ap-tr ${isSelected ? 'is-selected' : ''}`}
                                    >
                                        {/* Checkbox */}
                                        <td className="ap-td ap-td--checkbox">
                                            <input
                                                type="checkbox"
                                                checked={isSelected}
                                                onChange={() => handleSelectRow(row.cloud_reason_code_id)}
                                                className="ap-checkbox"
                                                aria-label={`Select ${row.reason_code_description}`}
                                                disabled={!rowEditable}
                                                title={!rowEditable ? 'Corporate-set reason codes can only be edited by corporate-level users' : undefined}
                                            />
                                        </td>

                                        {/* Row actions kebab — positioned next to checkbox */}
                                        <td className="ap-td ap-td--actions">
                                            <ReasonRowActions
                                                reason={row}
                                                onEdit={onEditReason}
                                                onDelete={handleSingleDelete}
                                                readOnly={!rowEditable}
                                            />
                                        </td>

                                        {/* Data cells */}
                                        {activeColumns.map((col) => {
                                            if (col.key === 'scope_label') {
                                                return (
                                                    <td key={col.key} className="ap-td">
                                                        <span
                                                            className={`ss-scope-badge ss-scope-badge--${row._isCorporate ? 'corporate' : 'community'}`}
                                                        >
                                                            {row.scope_label}
                                                        </span>
                                                    </td>
                                                );
                                            }
                                            if (col.key === 'reason_type_name') {
                                                const hasType = row.reason_type_name && row.reason_type_name !== '—';
                                                return (
                                                    <td key={col.key} className="ap-td">
                                                        {hasType ? (
                                                            <span className="ss-type-badge">
                                                                {row.reason_type_name}
                                                            </span>
                                                        ) : (
                                                            <span className="ap-cell-text">—</span>
                                                        )}
                                                    </td>
                                                );
                                            }
                                            return (
                                                <td key={col.key} className="ap-td">
                                                    <span className="ap-cell-text">{row[col.key] || '—'}</span>
                                                </td>
                                            );
                                        })}
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                </div>
            )}
        </>
    );
};

export default CloudReasonSettingsTab;
